import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import {
  BankTransactionLiteModel,
  useGetBankTransactions,
} from 'api-hooks/transactions/bank-transaction';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { Container, TopContainer } from 'components/styles';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import BankTransactionTable from './components/table';
import TransactionPillStatus from '../component/status';
import TransactionListTabs from '../component/transaction-list-tab';

export default function BankTransactionsList() {
  const { t } = useTranslation();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const formatDate = useDate();

  const _columns = React.useMemo<IColumn<BankTransactionLiteModel>[]>(
    () => [
      {
        header: t('common:status'),
        accessorKey: 'status',
        minSize: 150,
        cell: ({ getValue }) => (
          <TransactionPillStatus value={getValue() as any} />
        ),
      },
      {
        header: t('common:created_at'),
        accessorKey: 'createdAt',
        minSize: 150,
        maxSize: 150,
        sortName: 'created_at',
        cell: ({ getValue }) => (
          <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>
        ),
      },
      {
        header: t('common:transaction_at'),
        accessorKey: 'transactionAt',
        minSize: 150,
        sortName: 'transaction_at',
        cell: ({ getValue }) => {
          return <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>;
        },
      },
      {
        header: t('common:number'),
        accessorKey: 'number',
        minSize: 150,
      },
      {
        header: t('common:bank'),
        accessorKey: 'bankAccount',
        minSize: 300,
        cell: ({ row }) => {
          const { original } = row;
          return (
            <>{`Bank ${original.bankAccount.name} ${original.bankAccount.code} - ${original.bankAccount.accountNumber}`}</>
          );
        },
      },
      {
        header: t('common:category_name'),
        accessorKey: 'transactionCategory',
        sortName: 'transaction_category.name',
        minSize: 200,
        cell: ({ getValue }) => (getValue() as any)?.name || '-',
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 200,
      },
      {
        header: t('common:amount'),
        accessorKey: 'amount',
        minSize: 150,
        sortName: 'amount',
        textAlign: 'right',
        cell: ({ getValue }) => <>{string2money(getValue() as number)}</>,
      },
      {
        header: t('common:created_by'),
        accessorKey: 'createdBy.name',
        minSize: 150,
        sortName: 'created_by',
        cell: ({ getValue }) => getValue(),
      },
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetBankTransactions,
    {
      params: {
        page,
        filter: { status },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.BankTransactionsPage,
    });

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('bank-transactions');

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:bank_transaction')}
          showAdd={permission.create}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
          linkAdd={NavigationRoutes.BankTransactionsCreate}
        />

        <TransactionListTabs
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              setFilters();
            },
            setStatus,
            badge: dataNotification?.data,
          }}
        />
        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <BankTransactionTable
          bankTransaction={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
